import * as turf from '@turf/turf';
import { getType } from '@turf/turf';
import { sumBy } from 'lodash';
import {
  CROP_ID_FOR_NONE_CROP,
  CROP_ID_FOR_UNASSIGNED_STEMS,
  geometryTypes,
  NONE,
  sectionTypes,
  UNKNOWN_CROP,
} from '../constants/fields';
import { acresPerSqFt, acresPerSqMeter, feetPerMile } from '../constants/units';
import {
  cropVarietyIdForRow,
  getCropAndSpacingAtIdx,
  patternIdForRow,
} from './points-and-rows/rows';
import { cropVarietyIdForPattern, getTotalPatternFt } from './patterns';
import { notifyMonitoring } from './monitoring';
import { augmentFieldFeature } from './layerAugmenters';

// IMPORTANT! Increment this (integer-only) version number to force
// update any section crop meta changes to the server when the
// fields view is loaded.
export const SECTION_CROP_META_VERSION = 4;

export const getTotalRowLength = ({ rows }, { includeNoneRows = false } = {}) =>
  turf.featureReduce(
    rows,
    (sum, row) =>
      row.properties.cropName !== NONE || includeNoneRows
        ? sum + turf.length(row, { units: 'miles' })
        : sum,
    0,
  ) * feetPerMile;

const getAcreage = (section, { plantedArea = false } = {}) => {
  const { areaFraction, betweenRowSpacing, fieldFeature, type } = section;
  if (
    type === sectionTypes.LINE ||
    getType(fieldFeature) === geometryTypes.LINE_STRING
  ) {
    const totalRowLength = getTotalRowLength(section, {
      includeNoneRows: true,
    });
    return totalRowLength * betweenRowSpacing * acresPerSqFt;
  }
  // Augment fieldFeature for headlands
  const [innerFieldFeature, outerFieldFeature] =
    augmentFieldFeature(section)?.features || [];
  // Use inner feature for planted area, outer feature for field area
  const areaFeature = plantedArea ? innerFieldFeature : outerFieldFeature;
  return areaFeature
    ? turf.area(areaFeature.geometry) * acresPerSqMeter * (areaFraction || 1)
    : 0;
};

export const getSectionMeta = (section) => {
  const { rows, points } = section;
  if (!rows) return {};

  return {
    acreage: getAcreage(section),
    plantedAcreage: getAcreage(section, { plantedArea: true }),
    totalRowLength: getTotalRowLength(section),
    rowCount: rows.features.filter(
      ({ properties: { cropName } }) => cropName !== NONE,
    ).length,
    treeCount: points?.features?.length,
  };
};

export const isNoneOrUnassigned = (cropId) =>
  cropId === CROP_ID_FOR_UNASSIGNED_STEMS || cropId === CROP_ID_FOR_NONE_CROP;

/**
 * Determines the correct cropId.
 *
 * - if `crop.name == NONE`, we return `CROP_ID_FOR_NONE_CROP`
 * - if `crop.id` is defined, we return it
 * - if `crop.name` is in the `cropNameToId` mapping, we return the mapped ID
 * - otherwise, we return `includeUnassignedStems`
 *
 * NOTE: we check for the None crop *before* we check for crop ID since
 * there's a crop template with the name "None" in use currently and we don't
 * want to break it. 😭
 */
export const getCropId = (crop, cropNameToId = {}) => {
  const cropName = crop?.name;

  if (cropName === NONE) return CROP_ID_FOR_NONE_CROP;

  return crop?.id ?? cropNameToId[cropName] ?? CROP_ID_FOR_UNASSIGNED_STEMS;
};

// Returns a map from cropId to allocation fraction for the crops in a pattern.
// Values in the map should sum to 1.
const getAllocationFractionsForPattern = (patternId, inRowPatternsMap) => {
  const pattern = inRowPatternsMap[patternId];
  const totalPatternFt = getTotalPatternFt(pattern);
  return pattern.cropSequence.reduce(
    (result, { crop, spacingFt }) => ({
      ...result,
      [crop.id]: (result[crop.id] ?? 0) + spacingFt / totalPatternFt,
    }),
    {},
  );
};

// Returns a map from cropId to acreage fraction for the crops in the row.
// Values in the map should sum to 1. If rowIndex is negative, returns the
// allocation fractions for the primary crop or pattern, or an empty map if
// there is none.
const getAcreageFractionsForRow = (
  rowIndex,
  rowsData,
  section,
  { cropNameToId, inRowPatternsMap },
  options,
) => {
  const { includeUnassignedStems } = options;
  const { crop, primaryInRowPattern } = section;
  if (rowIndex < 0) {
    if (primaryInRowPattern?.id) {
      return getAllocationFractionsForPattern(
        primaryInRowPattern.id,
        inRowPatternsMap,
      );
    }
    const cropId = getCropId(crop, cropNameToId);
    return isNoneOrUnassigned(cropId) && !includeUnassignedStems
      ? {}
      : cropId
      ? { [cropId]: 1 }
      : { CROP_ID_FOR_UNASSIGNED_STEMS: 1 }; // TODO: Test this better
  }
  const rowData = rowsData[rowIndex] || {};
  const validCropDistribution = Object.values(rowData).some(
    ({ cropId, totalRowLength }) =>
      !isNoneOrUnassigned(cropId) && totalRowLength,
  );
  if (!validCropDistribution) {
    return getAcreageFractionsForRow(
      rowIndex - 1,
      rowsData,
      section,
      { cropNameToId, inRowPatternsMap },
      options,
    );
  }
  const filteredData = includeUnassignedStems
    ? Object.values(rowData)
    : Object.values(rowData).filter(
        ({ cropId }) => !isNoneOrUnassigned(cropId),
      );
  const rowLength = sumBy(filteredData, 'totalRowLength');
  return filteredData.reduce((result, { cropId, totalRowLength = 0 }) => {
    return { ...result, [cropId]: totalRowLength / rowLength };
  }, {});
};

const initStats = () => ({
  totalRowLength: 0,
  acreage: 0,
  plantedAcreage: 0,
  treeCount: 0,
  rowCount: 0,
  cropSectionVarieties: {},
});

// Transforms cropSectionVarieties map into array of { cropVarietyId, treeCount }
const transformCropSectionVarieties = (cropSection) => {
  const { cropSectionVarieties = {} } = cropSection;
  return Object.entries(
    cropSectionVarieties,
  ).map(([cropVarietyId, treeCount]) => ({ cropVarietyId, treeCount }));
};

// Merges two cropSectionVarieties maps of the format { varietyId: treeCount },
// summing treeCounts for matching varieties
const mergeCropSectionVarieties = (
  cropSectionVarieties1,
  cropSectionVarieties2,
) => {
  return Object.entries(cropSectionVarieties2).reduce(
    (cropSectionVarieties, [varietyId, treeCount]) => ({
      ...cropSectionVarieties,
      [varietyId]: (cropSectionVarieties[varietyId] ?? 0) + treeCount,
    }),
    cropSectionVarieties1,
  );
};

// Merges two cropSection data maps of the format
// { cropId:
//   { cropId, acreage, cropSectionVarieties,
//     rowCount, totalRowLength, treeCount }
// },
// summing values and merging cropSectionVarieties for matching crops
const mergeCropSectionData = (data1, data2) => {
  return Object.values(data2).reduce((data, cropData2) => {
    const {
      cropId,
      acreage = 0,
      cropSectionVarieties = {},
      plantedAcreage = 0,
      rowCount = 0,
      totalRowLength = 0,
      treeCount = 0,
    } = cropData2;
    const cropData1 = data[cropId] || { cropId, ...initStats() };
    return {
      ...data,
      [cropId]: {
        ...cropData1,
        acreage: (cropData1.acreage ?? 0) + acreage,
        plantedAcreage: (cropData1.plantedAcreage ?? 0) + plantedAcreage,
        cropSectionVarieties: mergeCropSectionVarieties(
          cropData1.cropSectionVarieties || {},
          cropSectionVarieties,
        ),
        rowCount: (cropData1.rowCount ?? 0) + rowCount,
        totalRowLength: (cropData1.totalRowLength ?? 0) + totalRowLength,
        treeCount: (cropData1.treeCount ?? 0) + treeCount,
      },
    };
  }, data1);
};

// Get cropSection data for a patterned row, excluding acreage
// NOTE: Assumes patterned rows start on first item in sequence
const getCropDataForPatternedRow = (
  treeCount,
  rowOverride,
  section,
  rowLength,
  patternId,
  { inRowPatternsMap },
) => {
  let allocatedRowLength = 0;
  let prevSpacingFt = 0;
  return [...Array(treeCount).keys()].reduce((byCrop, pointIndex) => {
    const isFirstPointInRow = pointIndex === 0;
    const isLastPointInRow = pointIndex === treeCount - 1;
    const { spacing: spacingFt, cropId } = getCropAndSpacingAtIdx(
      rowOverride,
      section,
      pointIndex,
      { cropMap: {}, cropNameToId: {}, inRowPatternsMap },
    );
    const allocationFt =
      // Allocate the rest of the row ft to the final point
      isLastPointInRow
        ? rowLength - allocatedRowLength
        : // Allocate half the distance on each side of the point.
          (prevSpacingFt + spacingFt) / 2;

    // Update values for next iteration
    allocatedRowLength += allocationFt;
    prevSpacingFt = spacingFt;
    // Notify monitoring if something is off
    if (allocatedRowLength > rowLength) {
      notifyMonitoring(
        `Invalid patterned row lengths for ${section.id}: allocated ${allocatedRowLength}, but total row length ${rowLength}`,
      );
    }

    const cropVarietyId = cropVarietyIdForPattern(
      patternId,
      inRowPatternsMap,
      cropId,
    );
    return mergeCropSectionData(byCrop, {
      [cropId]: {
        cropId,
        // Attribute the row to the first crop point in the row
        rowCount: isFirstPointInRow ? 1 : 0,
        treeCount: 1,
        totalRowLength: allocationFt,
        cropSectionVarieties: cropVarietyId ? { [cropVarietyId]: 1 } : {},
      },
    });
  }, {});
};

// Get cropSection for a simple section with no patterns or row overrides
const getCropSectionSimple = (
  section,
  { cropNameToId },
  { includeUnassignedStems },
) => {
  const { crop, primaryCropVariety } = section;
  const cropId = getCropId(crop, cropNameToId);
  if (isNoneOrUnassigned(cropId) && !includeUnassignedStems) return [];

  const cropSection = getSectionMeta(section);
  const { treeCount } = cropSection;
  const cropVarietyId = primaryCropVariety?.id;
  const cropSectionVarieties = cropVarietyId
    ? [{ cropVarietyId, treeCount }]
    : [];
  return [{ cropId, ...cropSection, cropSectionVarieties }];
};

// Get cropSection for a complex section with overrides and/or patterns
export const getCropSectionComplex = (
  section,
  { cropNameToId, cropVarietyIdToCropId, inRowPatternsMap },
  options,
) => {
  const { includeUnassignedStems } = options;
  const { crop, rowOverrides, rows } = section;
  const sectionAcreage = getAcreage(section);
  const sectionPlantedAcreage = getAcreage(section, { plantedArea: true });
  const sectionRowLengthWithNone = getTotalRowLength(section, {
    includeNoneRows: true,
  });

  const rowsData = turf.featureReduce(
    rows,
    (rowsMap, row, rowIdx) => {
      const { cropName = crop?.name, rowLength, treeCount } = row.properties;
      const cropId = getCropId({ name: cropName }, cropNameToId);
      const rowOverride = rowOverrides[rowIdx];
      const patternId = patternIdForRow(rowOverride, section);

      // MAIN STATS
      let rowMainData = {};
      if (patternId) {
        rowMainData = getCropDataForPatternedRow(
          treeCount,
          rowOverride,
          section,
          rowLength,
          patternId,
          { cropNameToId, inRowPatternsMap },
        );
      } else if (!isNoneOrUnassigned(cropId) || includeUnassignedStems) {
        const cropVarietyId = cropVarietyIdForRow(
          rowOverride,
          section,
          cropVarietyIdToCropId,
          cropId,
        );
        const cropSectionVarieties = cropVarietyId
          ? { [cropVarietyId]: treeCount }
          : {};
        rowMainData = {
          [cropId]: {
            cropId,
            cropSectionVarieties,
            rowCount: 1,
            // None rows should not be included in the row length
            totalRowLength: cropId === CROP_ID_FOR_NONE_CROP ? 0 : rowLength,
            treeCount,
          },
        };
      }

      // ACREAGE STATS
      const rowFraction = rowLength / sectionRowLengthWithNone;
      const rowAcreage = rowFraction * sectionAcreage;
      const rowPlantedAcreage = rowFraction * sectionPlantedAcreage;
      let rowAcreageData = {};
      if (cropId === CROP_ID_FOR_NONE_CROP) {
        // Get relative crop acreage fractions for previous row, then multiply
        // by the acreage for this row
        const acreageFractions = getAcreageFractionsForRow(
          rowIdx - 1,
          rowsMap,
          section,
          { cropNameToId, inRowPatternsMap },
          options,
        );
        rowAcreageData = Object.entries(acreageFractions).reduce(
          (result, [fractionCropId, acreageFraction]) => ({
            ...result,
            [fractionCropId]: {
              cropId: fractionCropId,
              acreage: acreageFraction * rowAcreage,
              plantedAcreage: acreageFraction * rowPlantedAcreage,
            },
          }),
          {},
        );
      } else {
        // For all the crops in this row:
        // - divide the crop row length by the section row length
        // - then multiply by the section acreage
        rowAcreageData = Object.values(rowMainData).reduce(
          (result, { cropId: rowCropId, totalRowLength }) => ({
            ...result,
            [rowCropId]: {
              cropId: rowCropId,
              acreage:
                (totalRowLength / sectionRowLengthWithNone) * sectionAcreage,
              plantedAcreage:
                (totalRowLength / sectionRowLengthWithNone) *
                sectionPlantedAcreage,
            },
          }),
          {},
        );
      }
      // Merge the main stats and acreage data
      return {
        ...rowsMap,
        [rowIdx]: mergeCropSectionData(rowMainData, rowAcreageData),
      };
    },
    {},
  );

  // Merge data from all rows into a single map
  const cropData = Object.values(rowsData).reduce(
    (result, rowData) => mergeCropSectionData(result, rowData),
    {},
  );

  return Object.values(cropData).map((cropSection) => ({
    ...cropSection,
    cropSectionVarieties: transformCropSectionVarieties(cropSection),
  }));
};

export const getSectionCropMeta = (
  section,
  dataMaps, // { cropNameToId, cropVarietyIdToCropId, inRowPatternsMap }
  options = { includeUnassignedStems: false },
) => {
  const { rows, rowOverrides, primaryInRowPattern } = section;
  if (!rows) return [];

  const hasOverrides = (rowOverrides || []).some((override) => override);
  return hasOverrides || primaryInRowPattern?.id
    ? getCropSectionComplex(section, dataMaps, options)
    : getCropSectionSimple(section, dataMaps, options);
};

/**
 * Aggregate cropSections from the backend by section, crop, and total.
 */
export const aggregateCropSections = ({ cropSections, cropsById }) => {
  /* eslint-disable no-param-reassign */
  const updateTotals = (totals, cropSection) => {
    [
      'totalRowLength',
      'acreage',
      'plantedAcreage',
      'rowCount',
      'treeCount',
    ].forEach((key) => {
      totals[key] = (totals[key] ?? 0) + (cropSection[key] ?? 0);
    });
  };

  const aggregates = cropSections.reduce(
    (
      { totals, totalsIncludingUnknownStems, byCrop, bySection },
      cropSection,
    ) => {
      const { cropId, sectionId } = cropSection;

      // Update the stats for this crop
      if (!byCrop[cropId]) byCrop[cropId] = { cropSectionVarieties: {} };
      updateTotals(byCrop[cropId], cropSection);
      // Update crop variety aggregates by crop
      (cropSection.cropSectionVarieties || []).forEach(
        ({ cropVarietyId, treeCount }) => {
          byCrop[cropId].cropSectionVarieties[cropVarietyId] =
            (byCrop[cropId].cropSectionVarieties[cropVarietyId] ?? 0) +
            treeCount;
        },
      );

      // Update the stats for this section
      if (!bySection[sectionId]) bySection[sectionId] = {};
      updateTotals(bySection[sectionId], cropSection);

      // Update the totals including unknown stems
      updateTotals(totalsIncludingUnknownStems, cropSection);

      // Update the total stats unless the crop is unknown
      if (!isNoneOrUnassigned(cropId)) {
        updateTotals(totals, cropSection);
      }

      return {
        totals,
        totalsIncludingUnknownStems,
        byCrop,
        bySection,
      };
    },
    {
      totals: {},
      totalsIncludingUnknownStems: {},
      byCrop: {},
      bySection: {},
    },
  );
  /* eslint-enable no-param-reassign */

  const cropData = Object.entries(aggregates.byCrop)
    .map(([cropId, cropSection]) => {
      const crop =
        cropsById[cropId] ??
        (cropId === CROP_ID_FOR_NONE_CROP ? { name: NONE } : UNKNOWN_CROP);

      return {
        cropId,
        crop,
        totalRowLength: cropSection.totalRowLength,
        treeCount: cropSection.treeCount,
        acreage: cropSection.acreage,
        plantedAcreage: cropSection.plantedAcreage,
        cropSectionVarieties: transformCropSectionVarieties(cropSection),
      };
    })
    // Sort this list so that the Unknown and None crops are last, and None is after Unknown:
    .sort((a, b) => {
      const toNum = ({ cropId }) => {
        switch (cropId) {
          case CROP_ID_FOR_UNASSIGNED_STEMS:
            return 1;
          case CROP_ID_FOR_NONE_CROP:
            return 2;
          default:
            return 0;
        }
      };
      return toNum(a) - toNum(b);
    });

  return {
    ...aggregates,
    cropData,
  };
};

/**
 * Get farm level aggregates and crop level aggregates for the farm.
 */
export const getFarmMeta = ({
  fieldSections,
  cropsById,
  cropNameToId,
  cropVarietyIdToCropId,
  inRowPatternsMap,
  includeHiddenSections = false,
  includeUnassignedStems = false,
}) => {
  const filteredSections = includeHiddenSections
    ? fieldSections
    : fieldSections.filter(({ shown }) => shown);
  const hiddenSections = fieldSections.filter(({ shown }) => !shown);

  const cropSections = filteredSections
    .map((section) =>
      getSectionCropMeta(
        section,
        { cropNameToId, cropVarietyIdToCropId, inRowPatternsMap },
        { includeUnassignedStems: true },
      ).map((meta) => ({
        ...meta,
        sectionId: section.id,
      })),
    )
    .flat();

  const aggregates = aggregateCropSections({
    cropSections,
    cropsById,
  });

  const hasUnassignedStems = aggregates.cropData.some(({ cropId }) =>
    isNoneOrUnassigned(cropId),
  );

  const totals = includeUnassignedStems
    ? aggregates.totalsIncludingUnknownStems
    : aggregates.totals;

  const sectionHasAnyAssignedStems = (section) =>
    cropSections.some(
      (cropSection) =>
        cropSection.sectionId === section.id &&
        !isNoneOrUnassigned(cropSection.cropId),
    );

  const sectionCount = includeUnassignedStems
    ? filteredSections.length
    : filteredSections.filter(sectionHasAnyAssignedStems).length;

  const cropData = includeUnassignedStems
    ? aggregates.cropData
    : aggregates.cropData.filter(({ cropId }) => !isNoneOrUnassigned(cropId));

  return {
    totals,
    sectionCount,
    hasUnassignedStems,
    cropData,
    sectionData: aggregates.bySection,
    hiddenSectionCount: hiddenSections.length,
  };
};
